<template>
    <Head title="Nadia 2.0" />
    <div class="flex h-screen bg-slate-50 text-slate-900">
        <!-- Conversation rail -->
        <aside class="flex w-72 flex-col border-r border-slate-200 bg-white">
            <div class="flex flex-col gap-2 border-b border-slate-200 px-4 py-4">
                <BaseLink href="/" class="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900" data-testid="back-to-nadia">
                    <span aria-hidden="true">←</span>
                    <span>Back to Nadia</span>
                </BaseLink>
                <div class="flex items-center justify-between">
                    <h1 class="text-base font-semibold">Nadia 2.0</h1>
                    <button
                        type="button"
                        class="rounded-md bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                        :disabled="creating"
                        data-testid="new-conversation"
                        @click="createConversation">
                        New
                    </button>
                </div>
                <div v-if="modelOptions.length > 0" class="flex items-center gap-2 text-[11px] text-slate-500">
                    <span class="font-medium uppercase tracking-wide text-slate-400">Model</span>
                    <select
                        v-model="selectedModel"
                        aria-label="Model for next new chat"
                        class="flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs"
                        data-testid="model-picker"
                        :disabled="creating"
                        @change="persistSelectedModel">
                        <option v-for="opt in modelOptions" :key="opt.value" :value="opt.value">
                            {{ opt.label }}<template v-if="opt.value === modelDefault"> (default)</template>
                        </option>
                    </select>
                </div>
            </div>
            <div class="flex-1 overflow-y-auto">
                <div v-if="loadingConversations" class="px-4 py-3 text-xs text-slate-500">Loading…</div>
                <div v-else-if="conversations.length === 0" class="px-4 py-3 text-xs text-slate-500">No conversations yet. Click <strong>New</strong>.</div>
                <button
                    v-for="conv in conversations"
                    :key="conv.id"
                    type="button"
                    class="block w-full border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50"
                    :class="{ 'bg-slate-100': activeConversationId === conv.id }"
                    data-testid="conversation-item"
                    @click="selectConversation(conv.id)">
                    <div class="truncate text-sm font-medium">{{ conv.title || "Untitled" }}</div>
                    <div class="flex items-center gap-2 text-xs text-slate-500">
                        <span>{{ formatDate(conv.updated_at) }}</span>
                        <span v-if="conv.model" class="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">
                            {{ labelForModel(conv.model) }}
                        </span>
                    </div>
                </button>
            </div>
            <div class="border-t border-slate-200 px-4 py-2 text-[10px] uppercase tracking-wide text-slate-400">Prototype · feature flag <code>nadia_v2</code></div>
        </aside>

        <!-- Active thread -->
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions - drag-and-drop file attach; keyboard users have the attach button -->
        <main
            class="relative flex flex-1 flex-col"
            @dragenter="upload_enabled && onDragEnter($event)"
            @dragover="upload_enabled && onDragOver($event)"
            @dragleave="upload_enabled && onDragLeave()"
            @drop="upload_enabled && onDrop($event)">
            <div v-if="upload_enabled && dragActive" class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-slate-900/30" data-testid="drop-overlay">
                <div class="rounded-lg border-2 border-dashed border-slate-400 bg-white/95 px-6 py-4 text-sm font-medium text-slate-700">Drop files to attach</div>
            </div>
            <div v-if="!activeConversationId" class="flex flex-1 items-center justify-center text-sm text-slate-500">Select or create a conversation to begin.</div>

            <template v-else>
                <div v-if="(viewer_enabled || skills_enabled) && activeConversationId" class="flex items-center justify-end gap-2 border-b border-slate-200 bg-white px-6 py-1.5">
                    <button
                        v-if="skills_enabled"
                        type="button"
                        class="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                        :class="{ 'bg-slate-100 border-slate-400': skillsInspectorOpen }"
                        data-testid="toggle-skills-inspector"
                        @click="skillsInspectorOpen = !skillsInspectorOpen">
                        Skills
                    </button>
                    <a
                        v-if="viewer_enabled"
                        :href="`/v2/viewer/${activeConversationId}/`"
                        target="_blank"
                        rel="noopener"
                        class="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                        data-testid="open-admin-viewer">
                        Open in admin viewer ↗
                    </a>
                </div>
                <RolePlayBanner v-if="activeRolePlay" :active="activeRolePlay" @submit="submitCardMessage" />
                <div ref="threadRef" class="flex-1 overflow-y-auto px-6 py-6">
                    <div class="mx-auto flex max-w-3xl flex-col gap-4">
                        <div v-if="loadingEvents" class="text-xs text-slate-500">Loading events…</div>
                        <template v-for="event in events" :key="event.id">
                            <UserBubble
                                v-if="event.event_type === 'user_message' && (event.data as UserMessageData).content"
                                :data="event.data as UserMessageData"
                                :tags="tagsForUserMessage(event.id)"
                                :coaching-notes="coachingNotesForUserMessage(event.id)" />
                            <AssistantBubble
                                v-else-if="event.event_type === 'assistant_message'"
                                :data="event.data as AssistantMessageData"
                                :event-id="event.id"
                                :conversation-id="activeConversationId"
                                :feedback-enabled="feedback_enabled"
                                :call-logs-enabled="call_logs_enabled"
                                :feedback="feedbackForEvent(event.id)"
                                :is-pending="event.id.startsWith('pending:')"
                                :in-role-play="Boolean(inRolePlayFor(event.id))"
                                :persona-name="inRolePlayFor(event.id)?.persona_name ?? null"
                                @feedback-saved="onFeedbackSaved"
                                @view-call-logs="onViewCallLogs" />
                            <ToolResultRow
                                v-else-if="event.event_type === 'tool_result' && !isInlineToolResult(event.data as ToolResultData)"
                                :data="event.data as ToolResultData"
                                @submit="submitCardMessage" />
                            <GuardrailCheckRow v-else-if="event.event_type === 'guardrail_check'" :data="event.data as GuardrailCheckData" />
                            <FileUploadRow v-else-if="event.event_type === 'file_upload'" :data="event.data as FileUploadData" />
                            <ErrorRow v-else-if="event.event_type === 'error'" :message="(event.data as { message: string }).message" />
                            <!-- developer_feedback events render as annotations under their targeted AssistantBubble; no top-level row. -->
                            <!-- role_play_started / role_play_ended drive the persistent banner + per-bubble chrome (see rolePlayState); no top-level row. -->
                            <!-- role_play_coaching_note renders as an annotation under the targeted UserBubble; no top-level row. -->
                        </template>

                        <div v-if="showThinking" class="flex items-center gap-2 text-xs text-slate-500">
                            <span class="h-2 w-2 animate-pulse rounded-full bg-slate-400"></span>
                            <span>Nadia is thinking…</span>
                        </div>
                    </div>
                </div>

                <form class="border-t border-slate-200 bg-white px-6 py-4" @submit.prevent="onSubmit">
                    <div class="mx-auto flex max-w-3xl flex-col gap-2">
                        <div class="flex items-center gap-2 text-xs text-slate-500">
                            <span class="font-medium uppercase tracking-wide text-slate-400">Dev</span>
                            <select
                                v-model="synthIntent"
                                aria-label="Synthesizer intent"
                                class="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
                                data-testid="synth-intent"
                                :disabled="synthesizing || streaming">
                                <option value="free_form">Free-form</option>
                                <option value="probe_retrieval">Probe retrieval</option>
                                <option value="probe_mutation">Probe mutation</option>
                                <option value="probe_promotion">Probe promotion</option>
                                <option value="probe_guardrail">Probe guardrail</option>
                                <option value="probe_skill">Probe skill</option>
                                <option value="probe_role_play">Probe role-play</option>
                                <option value="follow_up">Follow up</option>
                            </select>
                            <button
                                type="button"
                                class="rounded border border-slate-300 bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-50"
                                data-testid="synth-generate"
                                title="Generate a plausible next user message (shift-click to also send)"
                                :disabled="synthesizing || streaming"
                                @click="onSynthesize">
                                <span v-if="synthesizing">Generating…</span>
                                <span v-else>🪄 Generate</span>
                            </button>
                            <span class="text-slate-400">shift-click to send</span>
                            <span
                                v-if="ctxTokens != null"
                                class="ml-auto rounded border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-600"
                                data-testid="context-usage"
                                :title="usageTooltip">
                                ctx: {{ formatTokens(ctxTokens) }}<template v-if="contextLimit"> / {{ formatTokens(contextLimit) }}</template>
                                <template v-if="cacheHitTokens"> · cache: {{ formatTokens(cacheHitTokens) }}</template>
                                <template v-if="thinkingTokens"> · think: {{ formatTokens(thinkingTokens) }}</template>
                            </span>
                        </div>
                        <div v-if="upload_enabled && fileError" class="text-xs text-red-600">{{ fileError }}</div>
                        <div v-if="upload_enabled && pendingFiles.length > 0" class="flex flex-wrap gap-2">
                            <div
                                v-for="(pf, idx) in pendingFiles"
                                :key="idx"
                                class="flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700">
                                <span class="max-w-[120px] truncate">{{ pf.name }}</span>
                                <button type="button" class="text-slate-400 hover:text-slate-700" :disabled="uploading" @click="pendingFiles.splice(idx, 1)">×</button>
                            </div>
                        </div>
                        <div class="flex items-end gap-3">
                            <template v-if="upload_enabled">
                                <input
                                    ref="fileInputRef"
                                    type="file"
                                    class="hidden"
                                    aria-label="Upload file"
                                    :accept="acceptedFileTypes"
                                    multiple
                                    data-testid="file-input"
                                    @change="onFileSelected" />
                                <button
                                    type="button"
                                    class="rounded-md border border-slate-300 px-2 py-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
                                    title="Attach file"
                                    data-testid="attach-button"
                                    :disabled="streaming || uploading || !activeConversationId"
                                    @click="fileInputRef?.click()">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                </button>
                            </template>
                            <textarea
                                v-model="draft"
                                rows="2"
                                aria-label="Message"
                                placeholder="Message Nadia…"
                                class="flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none disabled:bg-slate-50"
                                data-testid="message-input"
                                :disabled="streaming"
                                @keydown.enter.exact.prevent="onSubmit"></textarea>
                            <MicTester />
                            <VoiceCallButton
                                v-if="activeConversationId"
                                :conversation-id="activeConversationId"
                                :disabled="streaming"
                                @ended="onVoiceEnded"
                                @stream-event="onVoiceStreamEvent" />
                            <button
                                type="submit"
                                class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                                data-testid="send-button"
                                :disabled="streaming || uploading || (draft.trim().length === 0 && pendingFiles.length === 0)">
                                Send
                            </button>
                        </div>
                    </div>
                </form>
            </template>
        </main>

        <!-- Staff-only LLM call-logs sidebar; opened on demand from a button on an
             assistant message, showing that message's call logs. -->
        <CallLogsSidebar
            v-if="call_logs_enabled && activeConversationId && callLogsEventId"
            :conversation-id="activeConversationId"
            :event-id="callLogsEventId"
            @close="callLogsEventId = null" />

        <!-- Staff-only skills inspector sidebar; toggled from the header bar. -->
        <SkillsInspector v-if="skills_enabled && skillsInspectorOpen" @close="skillsInspectorOpen = false" />
    </div>
</template>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import { fetchApi } from "~vue/api";
import jwtToken from "~vue/api/token.js";
import BaseLink from "~vue/router/BaseLink.vue";
import { logError } from "~vue/utils/logError";
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

import AssistantBubble from "../Nadia2Chat/AssistantBubble.vue";
import CallLogsSidebar from "../Nadia2Chat/CallLogsSidebar.vue";
import ErrorRow from "../Nadia2Chat/ErrorRow.vue";
import FileUploadRow from "../Nadia2Chat/FileUploadRow.vue";
import GuardrailCheckRow from "../Nadia2Chat/GuardrailCheckRow.vue";
import MicTester from "../Nadia2Chat/MicTester.vue";
import RolePlayBanner from "../Nadia2Chat/RolePlayBanner.vue";
import SkillsInspector from "../Nadia2Chat/SkillsInspector.vue";
import { streamCoachApiPost } from "../Nadia2Chat/sse";
import { INLINE_UI_KINDS } from "../Nadia2Chat/tool_ui_registry";
import ToolResultRow from "../Nadia2Chat/ToolResultRow.vue";
import type {
    AssistantMessageData,
    AssistantMessageDeltaData,
    Conversation,
    ConversationEvent,
    DeveloperFeedbackData,
    FileUploadData,
    GuardrailCheckData,
    PeopleTagsResult,
    PersonTag,
    RetractData,
    RolePlayCoachingNoteData,
    RolePlayStartedData,
    ToolCall,
    ToolResultData,
    UsageData,
    UserMessageData,
} from "../Nadia2Chat/types";
import UserBubble from "../Nadia2Chat/UserBubble.vue";
import type { VoiceStreamEvent } from "../Nadia2Chat/useVoiceSession";
import VoiceCallButton from "../Nadia2Chat/VoiceCallButton.vue";

const props = defineProps<{
    user_uuid: string;
    // Conversation to open on load, seeded by the /v2/chat/<id> route so a
    // refresh or shared link reopens that chat. Null on the bare /v2/chat URL.
    initial_conversation_id?: string | null;
    feedback_enabled?: boolean;
    call_logs_enabled?: boolean;
    viewer_enabled?: boolean;
    upload_enabled?: boolean;
    skills_enabled?: boolean;
}>();

// Keep the browser URL in sync with the active conversation so refresh,
// back/forward, and link-sharing all land on the right chat. We drive the
// History API directly (rather than an Inertia visit) because the conversation
// loads client-side via coach_api — there's no server round-trip to make.
const CHAT_BASE_PATH = "/v2/chat";

const pathForConversation = (id: string | null): string => (id ? `${CHAT_BASE_PATH}/${id}` : CHAT_BASE_PATH);

const conversationIdFromPath = (): string | null => {
    const match = globalThis.location.pathname.match(/\/v2\/chat\/([0-9a-fA-F-]{36})/);
    return match ? match[1] : null;
};

// Reflect the active conversation in the URL. ``replace`` swaps the current
// history entry (used on initial load) instead of pushing a new one. Preserve
// Inertia's existing history.state so its own back/forward handling keeps
// working — we only change the path.
const syncUrl = (id: string | null, { replace = false }: { replace?: boolean } = {}) => {
    const url = pathForConversation(id);
    if (globalThis.location.pathname === url) {
        return;
    }
    const state = globalThis.history.state;
    if (replace) {
        globalThis.history.replaceState(state, "", url);
    } else {
        globalThis.history.pushState(state, "", url);
    }
};

// Staff-only on-demand LLM call-logs sidebar. Holds the assistant event whose
// call logs are shown; null = sidebar closed. Set by the per-message "call logs"
// button, which is what triggers the fetch (nothing loads by default).
const callLogsEventId = ref<string | null>(null);

// Staff-only skills inspector sidebar. Toggled from the header bar.
const skillsInspectorOpen = ref(false);

const onViewCallLogs = (eventId: string) => {
    callLogsEventId.value = eventId;
};

const conversations = ref<Conversation[]>([]);
const loadingConversations = ref(false);
const activeConversationId = ref<string | null>(null);
const events = ref<ConversationEvent[]>([]);
const loadingEvents = ref(false);
const draft = ref("");
const streaming = ref(false);
const creating = ref(false);
const threadRef = ref<HTMLElement | null>(null);

// File upload state
const fileInputRef = ref<HTMLInputElement | null>(null);
const pendingFiles = ref<File[]>([]);
const uploading = ref(false);
const fileError = ref<string | null>(null);
const FILE_SIZE_LIMITS: Record<string, number> = {
    "image/jpeg": 3 * 1024 * 1024,
    "image/png": 3 * 1024 * 1024,
    "image/gif": 3 * 1024 * 1024,
    "image/webp": 3 * 1024 * 1024,
    "text/plain": 3 * 1024 * 1024,
    "text/csv": 3 * 1024 * 1024,
    "application/pdf": 6 * 1024 * 1024,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": 6 * 1024 * 1024,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": 3 * 1024 * 1024,
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": 50 * 1024 * 1024,
};
const DEFAULT_FILE_SIZE_LIMIT = 3 * 1024 * 1024;
const SUPPORTED_MIME_TYPES = new Set(Object.keys(FILE_SIZE_LIMITS));
const acceptedFileTypes = ".txt,.csv,.pdf,.docx,.xlsx,.pptx,.jpg,.jpeg,.png,.webp,.gif";

// Dev-only synthesizer state. The intent picker steers the LLM that generates
// the next user message — see coach_api/nadia2/synthesizer.py.
type SynthIntent = "free_form" | "probe_retrieval" | "probe_mutation" | "probe_promotion" | "probe_guardrail" | "probe_skill" | "probe_role_play" | "follow_up";
const synthIntent = ref<SynthIntent>("free_form");
const synthesizing = ref(false);

// Dev-only model picker state. ``modelDefault`` is the model
// ``resolve_chat_model`` would pick on the backend; ``selectedModel`` is what
// the dev has chosen for this browser session. Persisted in localStorage so
// the choice survives a refresh — but scoped to this page so it doesn't
// leak into production paths. When ``selectedModel === modelDefault`` we
// omit ``model_override`` from the request body so the backend can keep
// using its normal resolution.
interface ChatModelOption {
    value: string;
    label: string;
    // Mirrors the backend payload; null when the llmsdk catalog has no
    // confirmed limit. The dev pill hides the "/limit" suffix in that case.
    max_input_tokens?: number | null;
}
const MODEL_STORAGE_KEY = "nadia2.dev.modelOverride";
const modelOptions = ref<ChatModelOption[]>([]);
const modelDefault = ref<string>("");
const selectedModel = ref<string>("");

// Pending assistant bubble for in-flight token streaming. Keyed by turn_id —
// the backend assigns one per LLM call (agent step OR refusal). Cleared when
// the matching final assistant_message arrives.
//
// Pending events get a synthetic `id` of `pending:<turn_id>` so the v-for key
// stays stable across the delta -> finalize transition. The user can't tell
// the difference.
const pendingTurnId = ref<string | null>(null);

// Voice mode only: optimistic user bubble built from Pipecat onUserTranscript
// events (~1.5s before coach_api echoes the persisted user_message). Replaced
// in-place when the persisted event arrives so the bubble keeps a stable id.
const pendingUserEventId = ref<string | null>(null);

// Voice mode only: true between the user finishing speaking and Nadia's first
// streaming token, so the chat shows "Nadia is thinking..." during the round
// trip to coach_api.
const voiceTurnPending = ref(false);

const PENDING_PREFIX = "pending:";
const PENDING_USER_PREFIX = "pending-user:";

const showThinking = computed(() => streaming.value || voiceTurnPending.value);

const makePendingEvent = (turnId: string): ConversationEvent => ({
    id: `${PENDING_PREFIX}${turnId}`,
    sequence: events.value.length,
    event_type: "assistant_message",
    data: {
        content: "",
        thinking: null,
        reasoning_state: null,
        tool_calls: null,
        model: "",
        provider_id: "",
        stop_reason: null,
        retracted: false,
    } as AssistantMessageData,
    created_at: new Date().toISOString(),
});

const formatDate = (s: string): string => {
    try {
        return new Date(s).toLocaleString();
    } catch {
        return s;
    }
};

const scrollToBottom = async () => {
    await nextTick();
    if (threadRef.value) {
        threadRef.value.scrollTop = threadRef.value.scrollHeight;
    }
};

const loadChatModels = async () => {
    try {
        const response = await fetchApi("/v1/nadia2/models");
        if (!response.ok) {
            // Leave the picker hidden if the models endpoint is unavailable
            // for any reason rather than surfacing an error.
            return;
        }
        const payload = (await response.json()) as { current: string; options: ChatModelOption[] };
        modelOptions.value = payload.options;
        modelDefault.value = payload.current;
        // Prefer a previously-saved selection if it's still in the option set;
        // otherwise pre-select whatever the backend says is the current default.
        const saved = globalThis.localStorage?.getItem(MODEL_STORAGE_KEY);
        const isKnown = saved && payload.options.some((o) => o.value === saved);
        selectedModel.value = isKnown ? (saved as string) : payload.current;
    } catch (err) {
        logError(err instanceof Error ? err : new Error(String(err)));
    }
};

const persistSelectedModel = () => {
    if (!globalThis.localStorage) {
        return;
    }
    if (selectedModel.value && selectedModel.value !== modelDefault.value) {
        globalThis.localStorage.setItem(MODEL_STORAGE_KEY, selectedModel.value);
    } else {
        globalThis.localStorage.removeItem(MODEL_STORAGE_KEY);
    }
};

// Look up the friendly picker label for a model value. Falls back to the raw
// string so the rail still renders something for models that aren't in the
// curated picker list (e.g. older conversations pinned to a since-retired ID).
const labelForModel = (value: string): string => modelOptions.value.find((o) => o.value === value)?.label ?? value;

const loadConversations = async () => {
    loadingConversations.value = true;
    try {
        const response = await fetchApi("/v1/nadia2/conversations");
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        conversations.value = await response.json();
    } catch (err) {
        logError(err instanceof Error ? err : new Error(String(err)));
    } finally {
        loadingConversations.value = false;
    }
};

const loadEvents = async (conversationId: string) => {
    loadingEvents.value = true;
    try {
        const response = await fetchApi(`/v1/nadia2/conversations/${conversationId}/events`);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const allEvents: ConversationEvent[] = await response.json();
        // Hide retracted assistant messages on replay so the user sees the
        // same conversation shape as during the live stream.
        events.value = allEvents.filter((e) => {
            if (e.event_type !== "assistant_message") {
                return true;
            }
            return !(e.data as AssistantMessageData).retracted;
        });
        await scrollToBottom();
    } catch (err) {
        logError(err instanceof Error ? err : new Error(String(err)));
    } finally {
        loadingEvents.value = false;
    }
};

const applyDelta = (delta: AssistantMessageDeltaData) => {
    // Ensure a pending bubble exists for this turn_id.
    if (pendingTurnId.value !== delta.turn_id) {
        events.value.push(makePendingEvent(delta.turn_id));
        pendingTurnId.value = delta.turn_id;
    }
    const pendingId = `${PENDING_PREFIX}${delta.turn_id}`;
    const pending = events.value.find((e) => e.id === pendingId);
    if (!pending) {
        return;
    }
    const data = pending.data as AssistantMessageData;
    if (delta.content_delta) {
        data.content = (data.content ?? "") + delta.content_delta;
    }
    if (delta.tool_calls_delta) {
        const calls: ToolCall[] = (data.tool_calls ?? []).slice();
        for (const td of delta.tool_calls_delta) {
            while (calls.length <= td.index) {
                calls.push({ id: "", type: "function", function: { name: "", arguments: "" } });
            }
            const target = calls[td.index];
            if (td.id) {
                target.id = td.id;
            }
            if (!target.function) {
                target.function = { name: "", arguments: "" };
            }
            if (td.name) {
                target.function.name = td.name;
            }
            if (td.arguments_delta) {
                target.function.arguments = (target.function.arguments ?? "") + td.arguments_delta;
            }
        }
        data.tool_calls = calls;
    }
};

const finalizePending = (final: ConversationEvent) => {
    if (pendingTurnId.value === null) {
        // No pending bubble — append the final event directly.
        events.value.push(final);
        return;
    }
    const pendingId = `${PENDING_PREFIX}${pendingTurnId.value}`;
    const idx = events.value.findIndex((e) => e.id === pendingId);
    if (idx === -1) {
        events.value.push(final);
    } else {
        events.value[idx] = final;
    }
    pendingTurnId.value = null;
};

const handleRetract = (retract: RetractData) => {
    events.value = events.value.filter((e) => e.id !== retract.retracted_event_id);
    pendingTurnId.value = null;
};

const selectConversation = async (id: string, { updateUrl = true }: { updateUrl?: boolean } = {}) => {
    if (streaming.value) {
        return;
    }
    activeConversationId.value = id;
    events.value = [];
    if (updateUrl) {
        syncUrl(id);
    }
    await loadEvents(id);
};

const createConversation = async () => {
    creating.value = true;
    try {
        // Pin the chosen model on the conversation row so every turn in this
        // chat uses the same LLM. Send an empty string when the dev hasn't
        // picked something different from the resolved default — keeps the
        // production code path exercised end-to-end.
        const model = selectedModel.value && selectedModel.value !== modelDefault.value ? selectedModel.value : "";
        const response = await fetchApi("/v1/nadia2/conversations", {
            method: "POST",
            body: JSON.stringify({ title: "", model }),
        });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const created: Conversation = await response.json();
        conversations.value = [created, ...conversations.value];
        await selectConversation(created.id);
    } catch (err) {
        logError(err instanceof Error ? err : new Error(String(err)));
    } finally {
        creating.value = false;
    }
};

// Build a map of {user_message.id -> PersonTag[]} from any people_tags
// tool_result events later in the log. We attribute each tag tool_result to
// the most-recent prior user_message — the agent calls tag_people in the
// turn that responds to that user message, so this mapping is unambiguous.
const peopleTagsByUserEventId = computed<Record<string, PersonTag[]>>(() => {
    const map: Record<string, PersonTag[]> = {};
    let lastUserId: string | null = null;
    for (const ev of events.value) {
        if (ev.event_type === "user_message") {
            lastUserId = ev.id;
            continue;
        }
        if (ev.event_type !== "tool_result") {
            continue;
        }
        const data = ev.data as ToolResultData;
        if (data.ui_kind !== "people_tags" || data.error || !lastUserId) {
            continue;
        }
        const result = data.result as PeopleTagsResult | null;
        const tagged = result?.tagged ?? [];
        if (tagged.length === 0) {
            continue;
        }
        const bucket = map[lastUserId] ?? [];
        bucket.push(...tagged);
        map[lastUserId] = bucket;
    }
    return map;
});

const tagsForUserMessage = (eventId: string): PersonTag[] => peopleTagsByUserEventId.value[eventId] ?? [];

// Build a map of {target_event_id -> DeveloperFeedbackData[]} from any
// developer_feedback events in the log so we can render annotations beneath
// the targeted assistant bubble. Conversation-level feedback (target_event_id
// === null) is ignored here — it surfaces only via the CLI today.
const feedbackByEventId = computed<Record<string, DeveloperFeedbackData[]>>(() => {
    const map: Record<string, DeveloperFeedbackData[]> = {};
    for (const ev of events.value) {
        if (ev.event_type !== "developer_feedback") {
            continue;
        }
        const data = ev.data as DeveloperFeedbackData;
        const targetId = data.target_event_id;
        if (!targetId) {
            continue;
        }
        const bucket = map[targetId] ?? [];
        bucket.push(data);
        map[targetId] = bucket;
    }
    return map;
});

const feedbackForEvent = (eventId: string): DeveloperFeedbackData[] => feedbackByEventId.value[eventId] ?? [];

// Walk the event log forward and track role-play state. The active
// role-play (if any) is whatever ROLE_PLAY_STARTED hasn't been superseded
// by a matching ROLE_PLAY_ENDED. Each assistant message carries its own
// "was in role-play at this point" flag so the chrome stays correct on
// resume — a wrap-up message right after the role-play ended renders as
// out-of-character even though the persona was active a moment before.
interface RolePlayState {
    active: RolePlayStartedData | null;
    inRolePlayByAssistantId: Record<string, RolePlayStartedData>;
}

const rolePlayState = computed<RolePlayState>(() => {
    let active: RolePlayStartedData | null = null;
    const inRolePlayByAssistantId: Record<string, RolePlayStartedData> = {};
    for (const ev of events.value) {
        if (ev.event_type === "role_play_started") {
            active = ev.data as RolePlayStartedData;
        } else if (ev.event_type === "role_play_ended") {
            active = null;
        } else if (ev.event_type === "assistant_message" && active) {
            inRolePlayByAssistantId[ev.id] = active;
        }
    }
    return { active, inRolePlayByAssistantId };
});

const activeRolePlay = computed<RolePlayStartedData | null>(() => rolePlayState.value.active);

const inRolePlayFor = (eventId: string): RolePlayStartedData | null => rolePlayState.value.inRolePlayByAssistantId[eventId] ?? null;

// Coaching notes target a specific user_message event. The note tool
// already drops below-threshold ones before persisting, so anything we
// receive here should render. If multiple notes target the same message
// (rare; would mean note_role_play_turn was called more than once), they
// stack in chronological order.
const coachingNotesByUserEventId = computed<Record<string, RolePlayCoachingNoteData[]>>(() => {
    const map: Record<string, RolePlayCoachingNoteData[]> = {};
    for (const ev of events.value) {
        if (ev.event_type !== "role_play_coaching_note") {
            continue;
        }
        const data = ev.data as RolePlayCoachingNoteData;
        if (!data.target_event_id) {
            continue;
        }
        const bucket = map[data.target_event_id] ?? [];
        bucket.push(data);
        map[data.target_event_id] = bucket;
    }
    return map;
});

const coachingNotesForUserMessage = (eventId: string): RolePlayCoachingNoteData[] => coachingNotesByUserEventId.value[eventId] ?? [];

// Dev-bar context indicator: usage from the most recent finalized assistant
// message in the log. ``prompt_tokens`` is the size of the input the model
// saw on that turn (system prompt + projected history + tools), which is
// the most useful single signal for how close the conversation is getting
// to the context window. Skips the synthetic pending bubble during in-flight
// streaming (its data.usage is null until the final chunk arrives).
//
// We also track the model recorded on the same event so the limit lookup
// reflects what the agent actually ran — not what the conversation row pins
// (which can be empty when using the resolved default).
const latestAssistant = computed<AssistantMessageData | null>(() => {
    for (let i = events.value.length - 1; i >= 0; i--) {
        const ev = events.value[i];
        if (ev.event_type !== "assistant_message" || ev.id.startsWith(PENDING_PREFIX)) {
            continue;
        }
        return ev.data as AssistantMessageData;
    }
    return null;
});

const latestUsage = computed<UsageData | null>(() => latestAssistant.value?.usage ?? null);

// True context size at the most recent LLM call. Anthropic's ``input_tokens``
// (mapped to ``prompt_tokens``) only counts the non-cached portion when prompt
// caching is on — the full input is ``input_tokens + cache_read + cache_creation``.
// OpenAI's ``prompt_tokens`` already includes cached tokens (``cached_tokens``
// is a subset, not additive) and its cache_read/cache_creation fields are null,
// so the same formula works for both providers without branching.
const ctxTokens = computed<number | null>(() => {
    const u = latestUsage.value;
    if (!u) {
        return null;
    }
    return u.prompt_tokens + (u.cache_read_input_tokens ?? 0) + (u.cache_creation_input_tokens ?? 0);
});

// Context window cap for the model that ran the latest turn. Looked up by
// matching ``assistant_message.model`` against ``modelOptions`` — falls back
// to null when the picker doesn't include that model or the catalog had no
// confirmed limit (e.g. Opus 4.7's stub entry until pricing publishes).
const contextLimit = computed<number | null>(() => {
    const model = latestAssistant.value?.model;
    if (!model) {
        return null;
    }
    const opt = modelOptions.value.find((o) => o.value === model);
    return opt?.max_input_tokens ?? null;
});

// Cumulative cache hits across the entire conversation. Each step in a turn
// is its own LLM call with its own usage, so the "latest" value flickers as
// a turn progresses; summing across all events gives a monotonic signal
// (only grows, never disappears once present). Anthropic uses
// ``cache_read_input_tokens``; OpenAI uses ``cached_tokens``. Mutually
// exclusive in practice (one provider per turn) so a simple sum is correct.
const cacheHitTokens = computed<number | null>(() => {
    let sum = 0;
    for (const ev of events.value) {
        if (ev.event_type !== "assistant_message" || ev.id.startsWith(PENDING_PREFIX)) {
            continue;
        }
        const u = (ev.data as AssistantMessageData).usage;
        if (!u) {
            continue;
        }
        sum += u.cache_read_input_tokens ?? 0;
        sum += u.cached_tokens ?? 0;
    }
    return sum > 0 ? sum : null;
});

// Cumulative reasoning tokens. OpenAI reasoning models only — Anthropic folds
// extended thinking into ``output_tokens`` (rendered as completion) and
// doesn't surface a separable count, so this segment stays hidden for Claude
// turns. Cumulative for the same reason as cache: stable monotonic signal
// instead of per-step flicker.
const thinkingTokens = computed<number | null>(() => {
    let sum = 0;
    for (const ev of events.value) {
        if (ev.event_type !== "assistant_message" || ev.id.startsWith(PENDING_PREFIX)) {
            continue;
        }
        const u = (ev.data as AssistantMessageData).usage;
        if (!u || !u.reasoning_tokens) {
            continue;
        }
        sum += u.reasoning_tokens;
    }
    return sum > 0 ? sum : null;
});

const formatTokens = (n: number): string => {
    if (n >= 1000) {
        return `${(n / 1000).toFixed(1)}k`;
    }
    return String(n);
};

const usageTooltip = computed<string>(() => {
    const u = latestUsage.value;
    if (!u) {
        return "";
    }
    // Latest step's raw breakdown (what Anthropic/OpenAI actually returned)
    // plus the derived total + conversation cumulatives that drive the pill.
    const lines = ["— latest call —", `input (uncached): ${u.prompt_tokens}`, `completion: ${u.completion_tokens}`];
    if (u.cache_read_input_tokens != null) {
        lines.push(`cache read: ${u.cache_read_input_tokens}`);
    }
    if (u.cache_creation_input_tokens != null) {
        lines.push(`cache write: ${u.cache_creation_input_tokens}`);
    }
    if (u.cached_tokens != null) {
        lines.push(`cached (OpenAI): ${u.cached_tokens}`);
    }
    if (u.reasoning_tokens != null) {
        lines.push(`reasoning: ${u.reasoning_tokens}`);
    }
    if (ctxTokens.value != null) {
        lines.push(`total input: ${ctxTokens.value}`);
    }
    if (contextLimit.value != null) {
        lines.push(`window: ${contextLimit.value} (${latestAssistant.value?.model ?? "?"})`);
    }
    lines.push("— conversation —");
    if (cacheHitTokens.value != null) {
        lines.push(`cache hits: ${cacheHitTokens.value}`);
    }
    if (thinkingTokens.value != null) {
        lines.push(`reasoning: ${thinkingTokens.value}`);
    }
    return lines.join("\n");
});

// Cards (RolePlaySetupCard, RolePlayBanner) emit a synthetic follow-up
// user message when the user clicks Start / End. We submit it like a
// regular send. Skip if streaming so we don't double-up.
const submitCardMessage = async (payload: { message: string }) => {
    if (streaming.value) {
        return;
    }
    draft.value = payload.message;
    await onSubmit();
};

const onFeedbackSaved = (payload: { eventId: string; feedback: DeveloperFeedbackData }) => {
    // Append the new feedback as a synthetic developer_feedback event so the
    // bucket recomputes and the annotation appears immediately. The next page
    // refresh re-fetches it from the persisted event log.
    events.value.push({
        id: `pending-feedback:${crypto.randomUUID()}`,
        sequence: events.value.length,
        event_type: "developer_feedback",
        data: payload.feedback,
        created_at: new Date().toISOString(),
    });
};

const isInlineToolResult = (data: ToolResultData): boolean => Boolean(data.ui_kind && INLINE_UI_KINDS.has(data.ui_kind));

// Voice-only: ingest a Pipecat STT transcript and render the user bubble
// optimistically, before coach_api echoes the persisted user_message. With
// Whisper REST we get one final transcript per VAD-bounded segment so the
// bubble's content grows segment-by-segment; with a streaming STT we'd see
// partial transcripts (final=false) update in place.
const handleUserTranscript = (text: string, final: boolean): void => {
    if (!text) {
        return;
    }
    const existing = pendingUserEventId.value !== null ? events.value.find((e) => e.id === pendingUserEventId.value) : null;
    if (existing) {
        const data = existing.data as UserMessageData;
        if (final) {
            // Append a complete chunk; the previous partial-overlay (if any)
            // is cleared by truncating to the last final boundary tracked in
            // an attached marker.
            data.content = `${data.content} ${text}`.trim();
        } else {
            // Streaming partial: replace the trailing partial with the new
            // partial. We keep finals in `data._finalsContent` so we can
            // truncate the partial cleanly. (Not exposed elsewhere; the
            // bubble just renders `data.content`.)
            const finalsContent = (data as UserMessageData & { _finalsContent?: string })._finalsContent ?? data.content;
            (data as UserMessageData & { _finalsContent?: string })._finalsContent = finalsContent;
            data.content = finalsContent ? `${finalsContent} ${text}` : text;
        }
    } else {
        const id = `${PENDING_USER_PREFIX}${crypto.randomUUID()}`;
        pendingUserEventId.value = id;
        events.value.push({
            id,
            sequence: events.value.length,
            event_type: "user_message",
            data: { content: text } as UserMessageData,
            created_at: new Date().toISOString(),
        });
    }
    voiceTurnPending.value = true;
    scrollToBottom();
};

// Render a single live event from coach_api into the chat thread. Shared by
// text mode (SSE) and voice mode (RTVI server-message via nadia_voice) so a
// turn looks the same regardless of how it was initiated. Also handles the
// `user_transcript` pseudo-event the voice client emits on Pipecat STT.
const handleStreamEvent = ({ event, data }: { event: string; data: unknown }): void => {
    if (event === "user_transcript") {
        const payload = data as { text: string; final: boolean };
        handleUserTranscript(payload.text, payload.final);
        return;
    }
    if (event === "done") {
        voiceTurnPending.value = false;
        return;
    }
    if (event === "error") {
        const payload = data as { message?: string };
        events.value.push({
            id: crypto.randomUUID(),
            sequence: events.value.length,
            event_type: "error",
            data: { message: payload.message ?? "Unknown error" },
            created_at: new Date().toISOString(),
        });
        voiceTurnPending.value = false;
        scrollToBottom();
        return;
    }
    if (event === "assistant_message_delta") {
        // First token from Nadia — clear the thinking indicator so the bubble
        // takes over as the visible signal.
        voiceTurnPending.value = false;
        applyDelta(data as AssistantMessageDeltaData);
        scrollToBottom();
        return;
    }
    if (event === "retract") {
        handleRetract(data as RetractData);
        scrollToBottom();
        return;
    }
    // Standard persisted-event frames (user_message, assistant_message,
    // tool_result, guardrail_check).
    const persisted = data as ConversationEvent;
    if (persisted.event_type === "user_message" && pendingUserEventId.value !== null) {
        // Replace the optimistic voice bubble in place so the v-for key stays
        // stable and the canonical persisted id wins for downstream lookups
        // (e.g. people-tag attribution).
        const idx = events.value.findIndex((e) => e.id === pendingUserEventId.value);
        if (idx !== -1) {
            events.value[idx] = persisted;
            pendingUserEventId.value = null;
            scrollToBottom();
            return;
        }
    }
    if (persisted.event_type === "assistant_message") {
        voiceTurnPending.value = false;
        finalizePending(persisted);
    } else {
        events.value.push(persisted);
    }
    scrollToBottom();
};

const onVoiceStreamEvent = (payload: VoiceStreamEvent) => {
    handleStreamEvent(payload);
};

const onVoiceEnded = () => {
    // Reset any in-flight voice-mode UI state and re-fetch the persisted log
    // so the conversation matches the durable record once the call closes.
    pendingUserEventId.value = null;
    voiceTurnPending.value = false;
    if (activeConversationId.value) {
        loadEvents(activeConversationId.value);
    }
};

// Validate and stage files (shared by the file picker and drag-and-drop).
const addFiles = (files: File[]) => {
    fileError.value = null;
    const skipped: string[] = [];
    for (const file of files) {
        if (!SUPPORTED_MIME_TYPES.has(file.type)) {
            skipped.push(`${file.name}: unsupported file type`);
            continue;
        }
        const sizeLimit = FILE_SIZE_LIMITS[file.type] ?? DEFAULT_FILE_SIZE_LIMIT;
        if (file.size > sizeLimit) {
            const limitMB = Math.round(sizeLimit / (1024 * 1024));
            skipped.push(`${file.name}: exceeds ${limitMB}MB limit`);
            continue;
        }
        pendingFiles.value.push(file);
    }
    if (skipped.length > 0) {
        fileError.value = skipped.join("; ");
    }
};

const onFileSelected = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (!input.files) {
        return;
    }
    addFiles(Array.from(input.files));
    // Reset input so the same file can be re-selected
    input.value = "";
};

// Drag-and-drop. A depth counter handles dragenter/dragleave firing for nested
// children so the overlay only clears once the cursor truly leaves the area.
const dragActive = ref(false);
let dragDepth = 0;

const canAcceptDrop = (event: DragEvent): boolean =>
    Boolean(activeConversationId.value) && !streaming.value && !uploading.value && Boolean(event.dataTransfer?.types.includes("Files"));

const onDragEnter = (event: DragEvent) => {
    if (!canAcceptDrop(event)) {
        return;
    }
    event.preventDefault();
    dragDepth += 1;
    dragActive.value = true;
};

const onDragOver = (event: DragEvent) => {
    if (!canAcceptDrop(event)) {
        return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "copy";
    }
};

const onDragLeave = () => {
    if (dragDepth > 0) {
        dragDepth -= 1;
    }
    if (dragDepth === 0) {
        dragActive.value = false;
    }
};

const onDrop = (event: DragEvent) => {
    dragDepth = 0;
    dragActive.value = false;
    if (!activeConversationId.value || streaming.value || uploading.value || !event.dataTransfer) {
        return;
    }
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
        addFiles(files);
    }
};

const uploadPendingFiles = async (conversationId: string): Promise<boolean> => {
    if (pendingFiles.value.length === 0) {
        return true;
    }
    uploading.value = true;
    try {
        while (pendingFiles.value.length > 0) {
            const file = pendingFiles.value[0];
            const formData = new FormData();
            formData.append("file", file);
            // fetchApi forces Content-Type: application/json; for multipart
            // uploads we use raw fetch so the browser sets the boundary.
            const token = jwtToken.apiToken.value;
            const response = await fetch(`/v1/nadia2/conversations/${conversationId}/files`, {
                method: "POST",
                body: formData,
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            if (!response.ok) {
                // Raw fetch (multipart) bypasses useFetch's error extraction, so read
                // the two coach_api shapes inline. TODO(ENG-1600): drop the detail key.
                const errorData = await response.json().catch(() => ({}));
                // The catch below prefixes "File upload failed:", so the fallback is
                // just the status — matching main's copy for non-JSON bodies.
                throw new Error(errorData.error || errorData.detail || `HTTP ${response.status}`);
            }
            // Reflect the upload in the conversation stream immediately. The
            // persisted FILE_UPLOAD event precedes the user message but is not
            // replayed over the SSE turn, so we insert it optimistically here.
            // A later loadEvents() returns the same thin-pointer event in order.
            const uploaded = (await response.json()) as { id: string; file_id: string; filename: string; mimetype: string; file_size: number };
            events.value.push({
                id: uploaded.id,
                sequence: events.value.length,
                event_type: "file_upload",
                data: { file_id: uploaded.file_id, filename: uploaded.filename, mimetype: uploaded.mimetype, file_size: uploaded.file_size },
                created_at: new Date().toISOString(),
            });
            // Remove the successfully uploaded file so a later failure leaves
            // only the remaining (failed + not-yet-attempted) files in the list.
            pendingFiles.value.shift();
            scrollToBottom();
        }
        return true;
    } catch (err) {
        logError(err instanceof Error ? err : new Error(String(err)));
        events.value.push({
            id: crypto.randomUUID(),
            sequence: events.value.length,
            event_type: "error",
            data: { message: `File upload failed: ${err instanceof Error ? err.message : String(err)}` },
            created_at: new Date().toISOString(),
        });
        scrollToBottom();
        return false;
    } finally {
        uploading.value = false;
    }
};

const onSubmit = async () => {
    const content = draft.value.trim();
    const conversationId = activeConversationId.value;
    if ((!content && pendingFiles.value.length === 0) || !conversationId || streaming.value) {
        return;
    }

    // Upload files first (they become FILE_UPLOAD events in the conversation)
    if (pendingFiles.value.length > 0) {
        const uploadOk = await uploadPendingFiles(conversationId);
        if (!uploadOk) {
            return;
        }
    }

    // A file-only turn sends empty content: project_history attaches the
    // uploaded file blocks to this (empty) user message, so the LLM still
    // receives the file with no canned instruction injected on the user's
    // behalf. The empty UserBubble is suppressed in the template.
    draft.value = "";
    streaming.value = true;

    try {
        await streamCoachApiPost(`/v1/nadia2/conversations/${conversationId}/messages`, { content }, { onEvent: handleStreamEvent });
    } catch (err) {
        logError(err instanceof Error ? err : new Error(String(err)));
        events.value.push({
            id: crypto.randomUUID(),
            sequence: events.value.length,
            event_type: "error",
            data: { message: err instanceof Error ? err.message : String(err) },
            created_at: new Date().toISOString(),
        });
        scrollToBottom();
    } finally {
        streaming.value = false;
    }
};

// Dev-only: ask coach_api to generate a plausible next user message via LLM
// and drop it into the draft. Shift-click also auto-submits, so a tester can
// step through a synthetic conversation by repeatedly shift-clicking.
const onSynthesize = async (event: MouseEvent) => {
    const conversationId = activeConversationId.value;
    if (!conversationId || streaming.value || synthesizing.value) {
        return;
    }
    synthesizing.value = true;
    try {
        const response = await fetchApi(`/v1/nadia2/conversations/${conversationId}/synthesize-user-message`, {
            method: "POST",
            body: JSON.stringify({ intent: synthIntent.value }),
        });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const { text } = (await response.json()) as { text: string; intent: string };
        draft.value = text;
        if (event.shiftKey && text.trim().length > 0) {
            await onSubmit();
        }
    } catch (err) {
        logError(err instanceof Error ? err : new Error(String(err)));
    } finally {
        synthesizing.value = false;
    }
};

// Browser back/forward: re-sync the active conversation from the URL without
// pushing a new history entry. Falls back to the empty state when navigating
// back to the bare /v2/chat URL.
const onPopState = () => {
    if (streaming.value) {
        return;
    }
    const id = conversationIdFromPath();
    if (id) {
        if (id !== activeConversationId.value) {
            selectConversation(id, { updateUrl: false });
        }
    } else {
        activeConversationId.value = null;
        events.value = [];
    }
};

onMounted(async () => {
    await loadConversations();
    loadChatModels();
    // Open the deep-linked conversation (from /v2/chat/<id>) on load. The URL
    // is already correct, so don't push another history entry for it.
    const initialId = props.initial_conversation_id ?? conversationIdFromPath();
    if (initialId) {
        selectConversation(initialId, { updateUrl: false });
    } else {
        // Deep-link from Past Conversations / search results: ?conversation_id=<uuid>.
        // Falls back silently if the id is unknown so a stale link doesn't 404 —
        // server-side authz is still enforced when events load. selectConversation
        // normalizes the URL to /v2/chat/<id> via its default updateUrl.
        const deepLink = new URLSearchParams(globalThis.location.search).get("conversation_id");
        if (deepLink && conversations.value.some((c) => c.id === deepLink)) {
            await selectConversation(deepLink);
        }
    }
    globalThis.addEventListener("popstate", onPopState);
});

onBeforeUnmount(() => {
    globalThis.removeEventListener("popstate", onPopState);
});
</script>
